import axios from 'axios';
import { getAuditHeaders } from './clientInfo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add audit headers for tracking device/browser info
    const auditHeaders = getAuditHeaders();
    config.headers['X-Device-Type'] = auditHeaders['X-Device-Type'];
    config.headers['X-Browser-Info'] = auditHeaders['X-Browser-Info'];
    config.headers['X-OS-Info'] = auditHeaders['X-OS-Info'];
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo redirigir a login si es 401 Y NO es el endpoint de login
    // Esto evita el loop de redirección cuando las credenciales son incorrectas
    const isLoginEndpoint = error.config?.url?.includes('/auth/login');
    
    if (error.response?.status === 401 && !isLoginEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { 
      current_password: currentPassword, 
      new_password: newPassword 
    }),
  changePasswordExpired: (userId, currentPassword, newPassword) =>
    api.post('/auth/change-password-expired', {
      user_id: userId,
      current_password: currentPassword,
      new_password: newPassword
    }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/auth/register', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  importTemplate: () => api.get('/users/import/template', { responseType: 'blob' }),
  importUsers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const rolesAPI = {
  getAll: () => api.get('/roles'),
  getOne: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

export const menusAPI = {
  getAll: () => api.get('/menus'),
  create: (data) => api.post('/menus', data),
  update: (id, data) => api.put(`/menus/${id}`, data),
  delete: (id) => api.delete(`/menus/${id}`),
  reorder: (items) => api.put('/menus/reorder', { items }),
};

export const proyectosAPI = {
  getAll: () => api.get('/proyectos'),
  getOne: (id) => api.get(`/proyectos/${id}`),
  create: (data) => api.post('/proyectos', data),
  update: (id, data) => api.put(`/proyectos/${id}`, data),
};

// ============ Análisis de Ventas API ============
export const analisisVentasAPI = {
  tipologias: (proyecto, etapa) => {
    const params = new URLSearchParams({ proyecto });
    if (etapa) params.append('etapa', etapa);
    return api.get(`/analisis-ventas/tipologias?${params}`);
  },
  columnas: (proyecto, etapa, tipologia) => {
    const params = new URLSearchParams({ proyecto });
    if (etapa) params.append('etapa', etapa);
    if (tipologia && tipologia !== 'all') params.append('tipologia', tipologia);
    return api.get(`/analisis-ventas/columnas?${params}`);
  },
  dashboard: (proyecto, etapa, tipologia, columna) => {
    const params = new URLSearchParams({ proyecto });
    if (etapa) params.append('etapa', etapa);
    if (tipologia && tipologia !== 'all') params.append('tipologia', tipologia);
    if (columna && columna !== 'all') params.append('columna', columna);
    return api.get(`/analisis-ventas/dashboard?${params}`);
  },
  recaudacion: (proyecto, etapa, tipologia, columna) => {
    const params = new URLSearchParams({ proyecto });
    if (etapa) params.append('etapa', etapa);
    if (tipologia && tipologia !== 'all') params.append('tipologia', tipologia);
    if (columna && columna !== 'all') params.append('columna', columna);
    return api.get(`/analisis-ventas/recaudacion?${params}`);
  },
  evaluaciones: (proyecto, etapa, tipologia, columna) => {
    const params = new URLSearchParams({ proyecto });
    if (etapa) params.append('etapa', etapa);
    if (tipologia && tipologia !== 'all') params.append('tipologia', tipologia);
    if (columna && columna !== 'all') params.append('columna', columna);
    return api.get(`/analisis-ventas/evaluaciones?${params}`);
  },
  updatePanelControl: (payload) =>
    api.put('/analisis-ventas/panel-control', payload),
  listaProyectosEtapasPanel: () =>
    api.get('/analisis-ventas/panel-control/lista-proyectos-etapas'),
  diagnostico: (payload) => api.post('/analisis-ventas/diagnostico', payload),
  consultor: (payload) => api.post('/analisis-ventas/consultor', payload),
};

// ============ Lecciones Aprendidas API ============
export const leccionesAprendidasAPI = {  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v);
    });
    return api.get(`/lecciones-aprendidas?${params}`);
  },
  get: (id) => api.get(`/lecciones-aprendidas/${id}`),
  create: (data) => api.post('/lecciones-aprendidas', data),
  update: (id, data) => api.put(`/lecciones-aprendidas/${id}`, data),
  delete: (id) => api.delete(`/lecciones-aprendidas/${id}`),
  marcarLeida: (id) => api.post(`/lecciones-aprendidas/${id}/marcar-leida`),
  reportsOverview: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v);
    });
    return api.get(`/lecciones-aprendidas/reports/overview?${params}`);
  },
  downloadPlantilla: () =>
    api.get('/lecciones-aprendidas/excel/plantilla', { responseType: 'blob' }),
  exportXlsx: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, v);
    });
    return api.get(`/lecciones-aprendidas/excel/export?${params}`, {
      responseType: 'blob',
    });
  },
  importXlsx: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/lecciones-aprendidas/excel/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listSustentos: (leccionId) =>
    api.get(`/lecciones-aprendidas/${leccionId}/sustentos`),
  uploadSustento: (leccionId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/lecciones-aprendidas/${leccionId}/sustentos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteSustento: (leccionId, sustentoId) =>
    api.delete(`/lecciones-aprendidas/${leccionId}/sustentos/${sustentoId}`),
  topLectores: (limit = 5) =>
    api.get(`/lecciones-aprendidas/lectores/top?limit=${limit}`),
  rankingLectores: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.append(k, v);
    });
    return api.get(`/lecciones-aprendidas/lectores/ranking?${q}`);
  },
};

export const subdivisionesAPI = {
  getByProyecto: (proyectoId) => api.get(`/proyectos/${proyectoId}/subdivisiones`),
  create: (proyectoId, data) => api.post(`/proyectos/${proyectoId}/subdivisiones`, data),
  update: (id, data) => api.put(`/subdivisiones/${id}`, data),
  delete: (id) => api.delete(`/subdivisiones/${id}`),
  copiarFechaHijos: (id) => api.post(`/subdivisiones/${id}/copiar-fecha-hijos`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/proyectos/subdivisiones/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadPlantilla: () => api.get('/proyectos/subdivisiones/plantilla', { responseType: 'blob' }),
};

export const proveedoresAPI = {
  getAll: (params) => api.get('/proveedores', { params }),
  getById: (id) => api.get(`/proveedores/${id}`),
  create: (data) => api.post('/proveedores', data),
  update: (id, data) => api.put(`/proveedores/${id}`, data),
  delete: (id) => api.delete(`/proveedores/${id}`),
  exportExcel: () => api.get('/proveedores/export/excel', { responseType: 'blob' }),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/proveedores/import/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPorCategoria: (categoriaNombre) => api.get(`/proveedores/por-categoria/${encodeURIComponent(categoriaNombre)}`),
};

export const tipoContactoAPI = {
  getAll: () => api.get('/tipo-contacto'),
};

export const notificacionesContratistasAPI = {
  getEstado: (requerimientoId) => api.get(`/requerimientos/${requerimientoId}/estado-notificacion-contratistas`),
  notificar: (requerimientoId, auto = false) => api.post(`/requerimientos/${requerimientoId}/notificar-contratistas?auto=${auto}`),
  descargarPDF: (requerimientoId) => api.get(`/requerimientos/${requerimientoId}/acta-inspeccion-pdf`, { responseType: 'blob' }),
};

export const supervisoresAPI = {
  getAll: () => api.get('/supervisores'),
};

export const solicitudesAPI = {
  getAll: () => api.get('/solicitudes'),
  getPaginated: (params) => api.get('/solicitudes/paginated', { params }),
  getOne: (id) => api.get(`/solicitudes/${id}`),
  getDialogData: (id) => api.get(`/solicitudes/${id}/dialog-data`), // Endpoint optimizado
  create: (data) => api.post('/solicitudes', data),
  update: (id, data) => api.put(`/solicitudes/${id}`, data),
  addItem: (requerimientoId, data) => api.post(`/solicitudes/${requerimientoId}/items`, data),
  deleteItem: (requerimientoId, itemId) => api.delete(`/solicitudes/${requerimientoId}/items/${itemId}`),
};

export const uploadAPI = {
  uploadFoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/foto', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteFoto: (filePath) => api.delete('/upload/foto', { params: { file_path: filePath } }),
  updateSolicitudFotos: (requerimientoId, itemId, fotos) => 
    api.put(`/solicitudes/${requerimientoId}/items/${itemId}/fotos`, fotos),
  updateSolicitudItem: (requerimientoId, itemId, data) =>
    api.put(`/solicitudes/${requerimientoId}/items/${itemId}`, data),
  uploadActa: (inspeccionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/inspecciones/${inspeccionId}/acta`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const inspeccionesAPI = {
  get: (requerimientoId) => api.get(`/inspecciones/${requerimientoId}`),
  create: (data) => api.post('/inspecciones', data),
  update: (inspeccionId, data) => api.put(`/inspecciones/${inspeccionId}`, data),
  terminar: (inspeccionId) => api.post(`/inspecciones/${inspeccionId}/terminar`),
  enviarCorreo: (inspeccionId) => api.post(`/inspecciones/${inspeccionId}/enviar-correo`),
  deleteActa: (inspeccionId) => api.delete(`/inspecciones/${inspeccionId}/acta`),
};

export const atencionesAPI = {
  get: (requerimientoId) => api.get(`/atenciones/${requerimientoId}`),
  create: (data) => api.post('/atenciones', data),
  update: (atencionId, data) => api.put(`/atenciones/${atencionId}`, data),
  getSolicitudes: (atencionId) => api.get(`/atenciones/${atencionId}/solicitudes`),
  updateSolicitud: (atencionId, solicitudAtencionId, data) => 
    api.put(`/atenciones/${atencionId}/solicitudes/${solicitudAtencionId}`, data),
  terminarSolicitud: (atencionId, solicitudAtencionId) => 
    api.post(`/atenciones/${atencionId}/solicitudes/${solicitudAtencionId}/terminar`),
  createActaDiaria: (atencionId, solicitudAtencionId, data) => 
    api.post(`/atenciones/${atencionId}/solicitudes/${solicitudAtencionId}/acta-diaria`, data),
  uploadActa: (atencionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/atenciones/${atencionId}/acta`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteActa: (atencionId) => api.delete(`/atenciones/${atencionId}/acta`),
  terminar: (atencionId) => api.post(`/atenciones/${atencionId}/terminar`),
  enviarCorreo: (atencionId) => api.post(`/atenciones/${atencionId}/enviar-correo`),
};

export const actasDiariasAPI = {
  get: (actaId) => api.get(`/actas-diarias/${actaId}`),
  update: (actaId, data) => api.put(`/actas-diarias/${actaId}`, data),
  terminar: (actaId) => api.post(`/actas-diarias/${actaId}/terminar`),
  enviarCorreo: (actaId) => api.post(`/actas-diarias/${actaId}/enviar-correo`),
};

export const dashboardAPI = {
  getStats: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.supervisor_id) params.append('supervisor_id', filters.supervisor_id);
    if (filters.proyecto_id) params.append('proyecto_id', filters.proyecto_id);
    if (filters.etapa) params.append('etapa', filters.etapa);
    const queryString = params.toString();
    return api.get(`/dashboard/stats${queryString ? `?${queryString}` : ''}`);
  },
  // Endpoint unificado para carga rápida
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.supervisor_id) params.append('supervisor_id', filters.supervisor_id);
    if (filters.proyecto_id) params.append('proyecto_id', filters.proyecto_id);
    if (filters.etapas && filters.etapas.length > 0) params.append('etapas', filters.etapas.join(','));
    if (filters.origenes && filters.origenes.length > 0) params.append('origenes', filters.origenes.join(','));
    const queryString = params.toString();
    return api.get(`/dashboard/all${queryString ? `?${queryString}` : ''}`);
  },
};

export const configuracionAPI = {
  asignarProyectos: (data) => api.post('/configuracion/supervisores', data),
  getProyectosSupervisor: (supervisorId) => api.get(`/configuracion/supervisores/${supervisorId}`),
};

// Catálogos del Sistema
export const catalogosAPI = {
  // Categorías
  getCategorias: () => api.get('/catalogos/categorias'),
  createCategoria: (data) => api.post('/catalogos/categorias', data),
  updateCategoria: (id, data) => api.put(`/catalogos/categorias/${id}`, data),
  deleteCategoria: (id) => api.delete(`/catalogos/categorias/${id}`),
  deleteAllCategorias: () => api.delete('/catalogos/categorias/delete-all'),
  exportCategorias: () => api.get('/catalogos/categorias/export', { responseType: 'blob' }),
  importCategorias: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/catalogos/categorias/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Lugares
  getLugares: () => api.get('/catalogos/lugares'),
  createLugar: (data) => api.post('/catalogos/lugares', data),
  updateLugar: (id, data) => api.put(`/catalogos/lugares/${id}`, data),
  deleteLugar: (id) => api.delete(`/catalogos/lugares/${id}`),
  deleteAllLugares: () => api.delete('/catalogos/lugares/delete-all'),
  exportLugares: () => api.get('/catalogos/lugares/export', { responseType: 'blob' }),
  importLugares: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/catalogos/lugares/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Items
  getItems: () => api.get('/catalogos/items'),
  createItem: (data) => api.post('/catalogos/items', data),
  updateItem: (id, data) => api.put(`/catalogos/items/${id}`, data),
  deleteItem: (id) => api.delete(`/catalogos/items/${id}`),
  deleteAllItems: () => api.delete('/catalogos/items/delete-all'),
  exportItems: () => api.get('/catalogos/items/export', { responseType: 'blob' }),
  importItems: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/catalogos/items/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Fallas
  getFallas: () => api.get('/catalogos/fallas'),
  createFalla: (data) => api.post('/catalogos/fallas', data),
  updateFalla: (id, data) => api.put(`/catalogos/fallas/${id}`, data),
  deleteFalla: (id) => api.delete(`/catalogos/fallas/${id}`),
  deleteAllFallas: () => api.delete('/catalogos/fallas/delete-all'),
  exportFallas: () => api.get('/catalogos/fallas/export', { responseType: 'blob' }),
  importFallas: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/catalogos/fallas/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Asignación de Supervisores
export const asignacionesAPI = {
  getAll: () => api.get('/asignaciones/supervisores'),
  getBySupervisor: (supervisorId) => api.get(`/asignaciones/supervisor/${supervisorId}`),
  create: (data) => api.post('/asignaciones', data),
  update: (id, data) => api.put(`/asignaciones/${id}`, data),
  delete: (id) => api.delete(`/asignaciones/${id}`),
  bulkAssign: (data) => api.post('/asignaciones/bulk', data),
  exportExcel: () => api.get('/asignaciones/export/excel', { responseType: 'blob' }),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/asignaciones/import/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const exportAPI = {
  exportExcel: () => api.get('/export/excel', { responseType: 'blob' }),
  exportPDF: () => api.get('/export/pdf', { responseType: 'blob' }),
};

// Configuración General del Sistema
export const configuracionGeneralAPI = {
  get: () => api.get('/configuracion/general'),
  getPageData: () => api.get('/configuracion/page-data'), // Endpoint optimizado
  update: (data) => api.put('/configuracion/general', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/configuracion/general/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// NPS por WhatsApp
export const npsAPI = {
  enviar: (requerimientoId) => api.post(`/requerimientos/${requerimientoId}/enviar-nps`),
};


// ============ Parámetros API ============
export const parametrosAPI = {
  // Parámetros
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.grupo) params.append('grupo', filters.grupo);
    if (filters.activo !== undefined) params.append('activo', filters.activo);
    return api.get(`/parametros?${params}`);
  },
  getGrupos: () => api.get('/parametros/grupos'),
  getByGrupo: (grupo) => api.get(`/parametros/grupo/${grupo}`),
  get: (codigo) => api.get(`/parametros/${codigo}`),
  getValorSimple: (codigo) => api.get(`/parametros/${codigo}/valor`),
  create: (data) => api.post('/parametros', data),
  update: (codigo, data) => api.put(`/parametros/${codigo}`, data),
  delete: (codigo) => api.delete(`/parametros/${codigo}`),
  updateValorSimple: (codigo, valor) => api.put(`/parametros/${codigo}/valor-simple?nuevo_valor=${encodeURIComponent(valor)}`),
  
  // Valores
  getValores: (codigo) => api.get(`/parametros/${codigo}/valores`),
  addValor: (codigo, data) => api.post(`/parametros/${codigo}/valores`, data),
  updateValor: (valorId, data) => api.put(`/parametros/valores/${valorId}`, data),
  deleteValor: (valorId) => api.delete(`/parametros/valores/${valorId}`),
};


// ============ Integraciones API ============
export const integracionesAPI = {
  // Integraciones
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.activo !== undefined) params.append('activo', filters.activo);
    return api.get(`/integraciones?${params}`);
  },
  get: (codigo) => api.get(`/integraciones/${codigo}`),
  create: (data) => api.post('/integraciones', data),
  update: (codigo, data) => api.put(`/integraciones/${codigo}`, data),
  delete: (codigo) => api.delete(`/integraciones/${codigo}`),
  
  // Ejecución
  probar: (codigo) => api.post(`/integraciones/${codigo}/probar`),
  ejecutar: (codigo, datos = {}) => api.post(`/integraciones/${codigo}/ejecutar`, { datos }),
  
  // Valores
  getValores: (codigo) => api.get(`/integraciones/${codigo}/valores`),
  updateValores: (codigo, valores) => api.put(`/integraciones/${codigo}/valores`, valores),
  
  // Logs
  getLogs: (codigo, limit = 50) => api.get(`/integraciones/${codigo}/logs?limit=${limit}`),
};


// ============ Plugins API ============
export const pluginsAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    return api.get(`/plugins?${params}`);
  },
  get: (pluginId) => api.get(`/plugins/${pluginId}`),
  install: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/plugins/install', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  validate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/plugins/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  activate: (pluginId) => api.post(`/plugins/${pluginId}/activate`),
  deactivate: (pluginId) => api.post(`/plugins/${pluginId}/deactivate`),
  uninstall: (pluginId) => api.delete(`/plugins/${pluginId}`),
  download: (pluginId) => api.get(`/plugins/${pluginId}/download`, { responseType: 'blob' }),
  updateSettings: (pluginId, settings) => api.put(`/plugins/${pluginId}/settings`, settings),
  getHooks: () => api.get('/plugins/hooks'),
  getHookTypes: () => api.get('/plugins/hook-types'),
  downloadDemo: () => api.get('/plugins/demo/download', { responseType: 'blob' }),
  getDoc: (filename) => api.get(`/plugins/docs/${filename}`),
};

// ============ Hitos API ============
export const hitosAPI = {
  getAll: () => api.get('/hitos'),
  getById: (id) => api.get(`/hitos/${id}`),
  create: (data) => api.post('/hitos', data),
  update: (id, data) => api.put(`/hitos/${id}`, data),
  delete: (id) => api.delete(`/hitos/${id}`),
  toggle: (id) => api.put(`/hitos/${id}/toggle`),
  addDetalle: (hitoId, data) => api.post(`/hitos/${hitoId}/detalle`, data),
  updateDetalle: (detalleId, data) => api.put(`/hitos/detalle/${detalleId}`, data),
  deleteDetalle: (detalleId) => api.delete(`/hitos/detalle/${detalleId}`),
  toggleDetalle: (detalleId) => api.put(`/hitos/detalle/${detalleId}/toggle`),
  duplicar: (hitoId) => api.post(`/hitos/${hitoId}/duplicar`),
};

